import React, { useEffect, useMemo, useState } from "react";
import {
  FaJava, FaPython, FaReact, FaAws, FaDocker, FaShieldAlt, FaNodeJs, FaVuejs, FaAngular,
  FaApple, FaAndroid, FaKey, FaUserShield, FaLock, FaFingerprint, FaRobot, FaCubes, FaBolt,
  FaSitemap, FaProjectDiagram, FaFileContract, FaGlobe, FaLanguage, FaEye, FaSearch, FaMagic,
  FaBrain, FaMobileAlt, FaStream, FaExchangeAlt, FaCloud, FaDatabase, FaCode, FaLayerGroup,
  FaServer, FaCloudUploadAlt, FaVial, FaSitemap as FaSitemapAlt, FaLink
} from "react-icons/fa";
import {
  SiFlutter, SiSpring, SiKubernetes, SiTensorflow, SiPostgresql,
  SiApachekafka, SiJest, SiEthereum, SiGraphql, SiTerraform,
  SiNextdotjs, SiMongodb, SiRedis, SiPytorch, SiTailwindcss,
  SiSvelte, SiFastapi, SiFlask, SiDjango, SiMysql, SiElasticsearch,
  SiRabbitmq, SiCypress, SiTypescript, SiKotlin, SiGo, SiCplusplus, SiRust,
  SiGooglecloud, SiApachehadoop, SiApachespark,
  SiJsonwebtokens, SiJunit5, SiSelenium, SiPytest, SiSolidity, SiGithubactions, SiJavascript
} from "react-icons/si";


const categoryColors = {
  "Languages": "#f43f5e",
  "AI / Machine Learning": "#f59e0b",
  "Frontend": "#10b981",
  "Backend & APIs": "#3b82f6",
  "Cloud & DevOps": "#a855f7",
  "Databases": "#f97316",
  "Data Engineering": "#14b8a6",
  "Mobile Development": "#22c55e",
  "Security": "#ec4899",
  "Testing & Quality": "#6366f1",
  "Architecture": "#eab308",
  "Blockchain & Web3": "#06b6d4",
};

const categoryNodeIcons = {
  "Languages": <FaCode size={18} />,
  "AI / Machine Learning": <FaBrain size={18} />,
  "Frontend": <FaReact size={18} />,
  "Backend & APIs": <FaServer size={18} />,
  "Cloud & DevOps": <FaCloudUploadAlt size={18} />,
  "Databases": <FaDatabase size={18} />,
  "Data Engineering": <FaLayerGroup size={18} />,
  "Mobile Development": <FaMobileAlt size={18} />,
  "Security": <FaShieldAlt size={18} />,
  "Testing & Quality": <FaVial size={18} />,
  "Architecture": <FaSitemapAlt size={18} />,
  "Blockchain & Web3": <FaLink size={18} />,
};

const categoryShortLabels = {
  "Languages": "Languages",
  "AI / Machine Learning": "AI / ML",
  "Frontend": "Frontend",
  "Backend & APIs": "Backend",
  "Cloud & DevOps": "Cloud",
  "Databases": "Databases",
  "Data Engineering": "Data Eng",
  "Mobile Development": "Mobile",
  "Security": "Security",
  "Testing & Quality": "Testing",
  "Architecture": "Architecture",
  "Blockchain & Web3": "Web3",
};

const hexToRgba = (hex, alpha) => {
  const n = parseInt(hex.replace("#", ""), 16);
  const r = (n >> 16) & 255;
  const g = (n >> 8) & 255;
  const b = n & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const techIcons = [
  { icon: <FaJava size={16} className="text-orange-500" />, id: "Languages", name: "Java", desc: "Enterprise-grade backend language." },
  { icon: <SiTypescript size={16} className="text-blue-600" />, id: "Languages", name: "TypeScript", desc: "Typed superset of JavaScript." },
  { icon: <SiGo size={16} className="text-cyan-500" />, id: "Languages", name: "Go", desc: "Fast, compiled systems language." },
  { icon: <SiRust size={16} className="text-orange-700" />, id: "Languages", name: "Rust", desc: "Memory-safe systems programming." },
  { icon: <FaPython size={16} className="text-blue-500" />, id: "Languages", name: "Python", desc: "AI, scripting & backend language." },
  { icon: <SiJavascript size={16} className="text-yellow-500" />, id: "Languages", name: "JavaScript", desc: "The language of the web." },
  { icon: <SiKotlin size={16} className="text-purple-500" />, id: "Languages", name: "Kotlin", desc: "Modern JVM & Android language." },
  { icon: <SiCplusplus size={16} className="text-blue-700" />, id: "Languages", name: "C++", desc: "High-performance systems language." },

  { icon: <SiPytorch size={16} className="text-orange-600" />, id: "AI / Machine Learning", name: "PyTorch", desc: "Deep learning framework." },
  { icon: <FaBrain size={16} className="text-amber-600" />, id: "AI / Machine Learning", name: "LLMs", desc: "Large language models." },
  { icon: <FaMagic size={16} className="text-fuchsia-500" />, id: "AI / Machine Learning", name: "Generative AI", desc: "AI that creates new content." },
  { icon: <FaEye size={16} className="text-amber-500" />, id: "AI / Machine Learning", name: "Computer Vision", desc: "Teaching machines to see." },
  { icon: <SiTensorflow size={16} className="text-orange-500" />, id: "AI / Machine Learning", name: "TensorFlow", desc: "Machine learning platform." },
  { icon: <FaSearch size={16} className="text-amber-600" />, id: "AI / Machine Learning", name: "RAG", desc: "Retrieval-augmented generation." },
  { icon: <FaLanguage size={16} className="text-amber-500" />, id: "AI / Machine Learning", name: "NLP", desc: "Natural language processing." },

  { icon: <FaReact size={16} className="text-cyan-400" />, id: "Frontend", name: "React.js", desc: "UI development library." },
  { icon: <SiNextdotjs size={16} className="text-black" />, id: "Frontend", name: "Next.js", desc: "React framework for SSR." },
  { icon: <FaVuejs size={16} className="text-emerald-500" />, id: "Frontend", name: "Vue.js", desc: "Progressive UI framework." },
  { icon: <FaAngular size={16} className="text-red-600" />, id: "Frontend", name: "Angular", desc: "Enterprise web framework." },
  { icon: <SiTailwindcss size={16} className="text-sky-400" />, id: "Frontend", name: "Tailwind CSS", desc: "Utility-first CSS framework." },
  { icon: <SiSvelte size={16} className="text-orange-600" />, id: "Frontend", name: "Svelte", desc: "Compiler UI framework." },

  { icon: <FaNodeJs size={16} className="text-green-600" />, id: "Backend & APIs", name: "Node.js", desc: "JavaScript backend runtime." },
  { icon: <SiSpring size={16} className="text-emerald-500" />, id: "Backend & APIs", name: "Spring Boot", desc: "Java microservices." },
  { icon: <SiGraphql size={16} className="text-fuchsia-500" />, id: "Backend & APIs", name: "GraphQL", desc: "Flexible query APIs." },
  { icon: <SiFastapi size={16} className="text-teal-500" />, id: "Backend & APIs", name: "FastAPI", desc: "High-performance Python APIs." },
  { icon: <SiFlask size={16} className="text-slate-700" />, id: "Backend & APIs", name: "Flask", desc: "Lightweight Python framework." },
  { icon: <SiDjango size={16} className="text-emerald-800" />, id: "Backend & APIs", name: "Django", desc: "Full-stack Python web framework." },
  { icon: <FaExchangeAlt size={16} className="text-blue-500" />, id: "Backend & APIs", name: "REST APIs", desc: "Stateless resource-based APIs." },

  { icon: <FaAws size={16} className="text-amber-500" />, id: "Cloud & DevOps", name: "AWS", desc: "Cloud infrastructure platform." },
  { icon: <FaCloud size={16} className="text-sky-600" />, id: "Cloud & DevOps", name: "Azure", desc: "Microsoft's cloud platform." },
  { icon: <SiGooglecloud size={16} className="text-red-500" />, id: "Cloud & DevOps", name: "GCP", desc: "Google's cloud platform." },
  { icon: <FaDocker size={16} className="text-blue-500" />, id: "Cloud & DevOps", name: "Docker", desc: "Containerization platform." },
  { icon: <SiKubernetes size={16} className="text-blue-600" />, id: "Cloud & DevOps", name: "Kubernetes", desc: "Container orchestration." },
  { icon: <SiTerraform size={16} className="text-purple-600" />, id: "Cloud & DevOps", name: "Terraform", desc: "Infrastructure as Code." },
  { icon: <SiGithubactions size={16} className="text-slate-800" />, id: "Cloud & DevOps", name: "CI/CD", desc: "Automated build & deploy pipelines." },

  { icon: <SiPostgresql size={16} className="text-sky-600" />, id: "Databases", name: "PostgreSQL", desc: "Relational database system." },
  { icon: <SiMysql size={16} className="text-blue-600" />, id: "Databases", name: "MySQL", desc: "Relational database system." },
  { icon: <SiMongodb size={16} className="text-emerald-600" />, id: "Databases", name: "MongoDB", desc: "Document NoSQL database." },
  { icon: <SiRedis size={16} className="text-red-600" />, id: "Databases", name: "Redis", desc: "In-memory caching store." },
  { icon: <SiElasticsearch size={16} className="text-amber-600" />, id: "Databases", name: "Elasticsearch", desc: "Search & analytics engine." },
  { icon: <FaDatabase size={16} className="text-sky-700" />, id: "Databases", name: "DynamoDB", desc: "Managed NoSQL key-value store." },

  { icon: <SiApachehadoop size={16} className="text-yellow-600" />, id: "Data Engineering", name: "Hadoop", desc: "Distributed storage & processing." },
  { icon: <SiApachespark size={16} className="text-orange-600" />, id: "Data Engineering", name: "Spark", desc: "Large-scale data processing." },
  { icon: <SiApachekafka size={16} className="text-neutral-700" />, id: "Data Engineering", name: "Apache Kafka", desc: "Real-time streaming platform." },
  { icon: <SiRabbitmq size={16} className="text-orange-600" />, id: "Data Engineering", name: "RabbitMQ", desc: "Message broker system." },
  { icon: <FaStream size={16} className="text-teal-600" />, id: "Data Engineering", name: "ETL Pipelines", desc: "Extract, transform, load data." },

  { icon: <FaAndroid size={16} className="text-green-500" />, id: "Mobile Development", name: "Android", desc: "Google's mobile OS platform." },
  { icon: <FaApple size={16} className="text-slate-700" />, id: "Mobile Development", name: "iOS", desc: "Apple's mobile OS platform." },
  { icon: <FaMobileAlt size={16} className="text-emerald-500" />, id: "Mobile Development", name: "React Native", desc: "Cross-platform native apps." },
  { icon: <SiFlutter size={16} className="text-blue-400" />, id: "Mobile Development", name: "Flutter", desc: "Cross-platform mobile apps." },
  { icon: <SiKotlin size={16} className="text-purple-400" />, id: "Mobile Development", name: "Kotlin Multiplatform", desc: "Shared code across platforms." },

  { icon: <FaShieldAlt size={16} className="text-pink-500" />, id: "Security", name: "OAuth 2.0", desc: "Delegated authorization standard." },
  { icon: <FaKey size={16} className="text-pink-600" />, id: "Security", name: "SAML", desc: "Single sign-on standard." },
  { icon: <SiJsonwebtokens size={16} className="text-fuchsia-600" />, id: "Security", name: "JWT", desc: "Compact signed access tokens." },
  { icon: <FaUserShield size={16} className="text-pink-500" />, id: "Security", name: "IAM", desc: "Identity & access management." },
  { icon: <FaLock size={16} className="text-pink-600" />, id: "Security", name: "Encryption", desc: "Protecting data at rest & transit." },
  { icon: <FaFingerprint size={16} className="text-rose-500" />, id: "Security", name: "Zero Trust", desc: "Never trust, always verify." },

  { icon: <SiJunit5 size={16} className="text-emerald-700" />, id: "Testing & Quality", name: "JUnit", desc: "Java unit testing framework." },
  { icon: <SiSelenium size={16} className="text-emerald-600" />, id: "Testing & Quality", name: "Selenium", desc: "Browser automation & testing." },
  { icon: <SiCypress size={16} className="text-slate-800" />, id: "Testing & Quality", name: "Cypress", desc: "End-to-end web testing." },
  { icon: <SiJest size={16} className="text-red-500" />, id: "Testing & Quality", name: "Jest", desc: "JavaScript unit testing." },
  { icon: <SiPytest size={16} className="text-blue-600" />, id: "Testing & Quality", name: "PyTest", desc: "Python testing framework." },
  { icon: <FaRobot size={16} className="text-indigo-500" />, id: "Testing & Quality", name: "Automation", desc: "Automated test workflows." },

  { icon: <FaCubes size={16} className="text-yellow-500" />, id: "Architecture", name: "Microservices", desc: "Independently deployable services." },
  { icon: <FaBolt size={16} className="text-yellow-600" />, id: "Architecture", name: "Event-Driven", desc: "Systems reacting to events." },
  { icon: <FaSitemap size={16} className="text-yellow-500" />, id: "Architecture", name: "System Design", desc: "Planning scalable architecture." },
  { icon: <FaProjectDiagram size={16} className="text-yellow-600" />, id: "Architecture", name: "Distributed Systems", desc: "Systems spanning many machines." },

  { icon: <FaFileContract size={16} className="text-cyan-600" />, id: "Blockchain & Web3", name: "Smart Contracts", desc: "Self-executing on-chain code." },
  { icon: <SiEthereum size={16} className="text-indigo-500" />, id: "Blockchain & Web3", name: "Ethereum", desc: "Decentralized Web3 platform." },
  { icon: <SiSolidity size={16} className="text-slate-700" />, id: "Blockchain & Web3", name: "Solidity", desc: "Smart contract language." },
  { icon: <FaGlobe size={16} className="text-cyan-500" />, id: "Blockchain & Web3", name: "Web3 APIs", desc: "Interfacing with blockchain data." },
];

const categoryDescriptions = {
  "Languages": "Core programming languages powering everything from enterprise backends to systems-level performance.",
  "AI / Machine Learning": "Building intelligent systems — from LLMs and RAG pipelines to computer vision and deep learning.",
  "Frontend": "Crafting fast, accessible, and delightful user interfaces with modern component-based frameworks.",
  "Backend & APIs": "Designing robust services and APIs that power scalable, maintainable applications.",
  "Cloud & DevOps": "Provisioning, deploying, and scaling infrastructure with automation-first cloud practices.",
  "Databases": "Storing and querying data reliably across relational, document, and in-memory systems.",
  "Data Engineering": "Moving and transforming data at scale through pipelines, streaming, and messaging systems.",
  "Mobile Development": "Building native and cross-platform mobile experiences for iOS and Android.",
  "Security": "Protecting systems and data with modern authentication, authorization, and encryption standards.",
  "Testing & Quality": "Ensuring reliability through automated unit, integration, and end-to-end testing.",
  "Architecture": "Designing resilient, distributed systems that scale gracefully with business needs.",
  "Blockchain & Web3": "Building decentralized applications and smart contracts on modern blockchain platforms.",
};

const features = [
  { title: "Languages", tags: ["Java", "Python", "JavaScript", "TypeScript", "Kotlin", "Go", "C++", "Rust"], accent: "from-rose-500 to-pink-500", badgeBg: "bg-rose-50 text-rose-700 border-rose-100", circle: "bg-rose-500" },
  { title: "AI / Machine Learning", tags: ["LLMs", "RAG", "Generative AI", "PyTorch", "NLP", "Computer Vision", "TensorFlow"], accent: "from-amber-400 to-orange-500", badgeBg: "bg-amber-50 text-amber-700 border-amber-100", circle: "bg-amber-500", hot: true },
  { title: "Frontend", tags: ["React.js", "Next.js", "Vue.js", "Angular", "Svelte", "Tailwind CSS"], accent: "from-emerald-400 to-teal-500", badgeBg: "bg-emerald-50 text-emerald-700 border-emerald-100", circle: "bg-emerald-500" },
  { title: "Backend & APIs", tags: ["Node.js", "Spring Boot", "FastAPI", "Flask", "Django", "REST APIs", "GraphQL"], accent: "from-blue-400 to-indigo-500", badgeBg: "bg-blue-50 text-blue-700 border-blue-100", circle: "bg-blue-500" },
  { title: "Cloud & DevOps", tags: ["AWS", "Azure", "GCP", "Docker", "Kubernetes", "Terraform", "CI/CD"], accent: "from-purple-400 to-violet-500", badgeBg: "bg-purple-50 text-purple-700 border-purple-100", circle: "bg-purple-500" },
  { title: "Databases", tags: ["PostgreSQL", "MySQL", "MongoDB", "Redis", "Elasticsearch", "DynamoDB"], accent: "from-orange-400 to-amber-500", badgeBg: "bg-orange-50 text-orange-700 border-orange-100", circle: "bg-orange-500" },
  { title: "Data Engineering", tags: ["Hadoop", "Spark", "Apache Kafka", "RabbitMQ", "ETL Pipelines"], accent: "from-teal-400 to-cyan-500", badgeBg: "bg-teal-50 text-teal-700 border-teal-100", circle: "bg-teal-500" },
  { title: "Mobile Development", tags: ["Android", "iOS", "React Native", "Flutter", "Kotlin Multiplatform"], accent: "from-green-400 to-emerald-500", badgeBg: "bg-green-50 text-green-700 border-green-100", circle: "bg-green-500" },
  { title: "Security", tags: ["OAuth 2.0", "SAML", "JWT", "IAM", "Encryption", "Zero Trust"], accent: "from-pink-400 to-rose-500", badgeBg: "bg-pink-50 text-pink-700 border-pink-100", circle: "bg-pink-500" },
  { title: "Testing & Quality", tags: ["JUnit", "Selenium", "Cypress", "Jest", "PyTest", "Automation"], accent: "from-indigo-400 to-blue-500", badgeBg: "bg-indigo-50 text-indigo-700 border-indigo-100", circle: "bg-indigo-500" },
  { title: "Architecture", tags: ["Microservices", "Event-Driven", "System Design", "Distributed Systems"], accent: "from-yellow-400 to-amber-500", badgeBg: "bg-yellow-50 text-yellow-700 border-yellow-100", circle: "bg-yellow-500" },
  { title: "Blockchain & Web3", tags: ["Smart Contracts", "Ethereum", "Solidity", "Web3 APIs"], accent: "from-cyan-400 to-blue-500", badgeBg: "bg-cyan-50 text-cyan-700 border-cyan-100", circle: "bg-cyan-500" },
];

const toRad = (deg) => (deg * Math.PI) / 180;
const ellipsePoint = (angleDeg, rx, ry) => {
  const rad = toRad(angleDeg);
  return { x: rx * Math.cos(rad), y: ry * Math.sin(rad) };
};

const Technologies = () => {
  // Increased container dimensions for a larger, more spacious visual display
  const [dims, setDims] = useState({ w: 520, h: 520 });
  const [activeCategory, setActiveCategory] = useState(null);
  const [hoveredIcon, setHoveredIcon] = useState(null);
  const [rotationOffset, setRotationOffset] = useState(0);

  useEffect(() => {
    const update = () => {
      if (window.innerWidth < 400) setDims({ w: 340, h: 340 });
      else if (window.innerWidth < 640) setDims({ w: 420, h: 420 });
      else if (window.innerWidth < 1024) setDims({ w: 460, h: 460 });
      else setDims({ w: 540, h: 540 });
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Smooth rotation animation, pauses when user interacts with an element
  useEffect(() => {
    let animId;
    let lastTime = performance.now();

    const animate = (time) => {
      const delta = time - lastTime;
      lastTime = time;
      if (!activeCategory && !hoveredIcon) {
        setRotationOffset((prev) => (prev + delta * 0.012) % 360);
      }
      animId = requestAnimationFrame(animate);
    };

    animId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animId);
  }, [activeCategory, hoveredIcon]);

  const { w, h } = dims;

  // Larger radius definitions
  const R1 = Math.min(w, h) * 0.35;
  const R2 = Math.min(w, h) * 0.46;

  const iconsByCategory = useMemo(() => {
    const map = {};
    techIcons.forEach((item) => {
      if (!map[item.id]) map[item.id] = [];
      map[item.id].push(item);
    });
    return map;
  }, []);

  const categoryOrder = features.map((f) => f.title);

  const nodes = useMemo(() => {
    const total = categoryOrder.length;
    return categoryOrder.map((title, i) => {
      const angle = -90 + (360 / total) * i + rotationOffset;
      const pos = ellipsePoint(angle, R1, R1);
      return { title, angle, x: pos.x, y: pos.y };
    });
  }, [R1, categoryOrder, rotationOffset]);

  const activeNode = nodes.find((n) => n.title === activeCategory);

  const bloomIcons = useMemo(() => {
    if (!activeNode) return [];
    const items = iconsByCategory[activeNode.title] || [];
    const count = items.length;
    const spread = Math.min(130, Math.max(50, count * 18));
    return items.map((item, idx) => {
      const offset = count === 1 ? 0 : -spread / 2 + (spread / (count - 1)) * idx;
      const angle = activeNode.angle + offset;
      const pos = ellipsePoint(angle, R2, R2);
      return { ...item, x: pos.x, y: pos.y };
    });
  }, [activeNode, iconsByCategory, R2]);

  const activeFeature = features.find((f) => f.title === activeCategory);

  const hubContent = hoveredIcon
    ? { type: "tech", icon: hoveredIcon.icon, title: hoveredIcon.name, desc: hoveredIcon.desc, color: categoryColors[hoveredIcon.id] }
    : activeFeature
    ? { type: "category", title: activeFeature.title, desc: categoryDescriptions[activeFeature.title], color: categoryColors[activeFeature.title] }
    : null;

  const selectCategory = (title) => {
    setActiveCategory(title);
  };

  const clearCategory = () => {
    setActiveCategory(null);
    setHoveredIcon(null);
  };

  const handleTagHover = (categoryTitle, tagName) => {
    setActiveCategory(categoryTitle);
    const matchedIcon = techIcons.find(
      (item) => item.id === categoryTitle && item.name.toLowerCase() === tagName.toLowerCase()
    );
    if (matchedIcon) {
      setHoveredIcon(matchedIcon);
    }
  };

  return (
    <section className="w-full bg-slate-50/50 py-20 px-4 sm:px-6 lg:px-8 font-sans">
      <style>{`
        @keyframes hubPulse {
          0%, 100% { box-shadow: 0 15px 35px -10px rgba(79,70,229,0.25); }
          50% { box-shadow: 0 15px 35px -10px rgba(79,70,229,0.45); }
        }
        .tech-hub { animation: hubPulse 3.4s ease-in-out infinite; }
        @keyframes bloomIn {
          from { opacity: 0; transform: scale(0.3); }
          to { opacity: 1; transform: scale(1); }
        }
        .bloom-icon-inner { animation: bloomIn 0.28s cubic-bezier(0.34,1.56,0.64,1) backwards; }
      `}</style>

      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-12 items-start relative">

        {/* Left Column: Radial Category Wheel */}
        <div className="lg:col-span-5 flex flex-col items-center lg:items-start lg:sticky lg:top-12 lg:self-start z-10">
          <span className="inline-block text-blue-600 uppercase tracking-[4px] font-semibold">
            Capabilities & Stack
          </span>
          <h2 className="mt-4 text-4xl md:text-5xl font-bold text-gray-900">
            Our Technology Expertise
          </h2>
          <p className="max-w-4xl mx-auto mt-8 text-lg text-gray-600 leading-relaxed">
            Hover any node or individual sub-tool on the right to observe its details in the hub.
          </p>

          <div
            className="relative mt-6 mx-auto flex items-center justify-center select-none overflow-visible"
            style={{ width: w, height: h }}
          >
            {/* Circle Guides */}
            <svg
              className="absolute inset-0 w-full h-full pointer-events-none z-10 overflow-visible"
              viewBox={`0 0 ${w} ${h}`}
            >
              <circle
                cx={w / 2} cy={h / 2} r={R1}
                fill="none" stroke="#cbd5e1" strokeWidth="2" strokeDasharray="4 8"
              />
              {activeNode && (
                <>
                  <circle
                    cx={w / 2} cy={h / 2} r={R2}
                    fill="none" stroke={hexToRgba(categoryColors[activeNode.title], 0.3)} strokeWidth="2.5" strokeDasharray="3 6"
                  />
                  {bloomIcons.map((icon, i) => (
                    <line
                      key={i}
                      x1={w / 2 + activeNode.x}
                      y1={h / 2 + activeNode.y}
                      x2={w / 2 + icon.x}
                      y2={h / 2 + icon.y}
                      stroke={hexToRgba(categoryColors[activeNode.title], 0.4)}
                      strokeWidth="2"
                    />
                  ))}
                </>
              )}
            </svg>

            {/* Orbiting Category Nodes */}
            {nodes.map((node) => {
              const color = categoryColors[node.title];
              const isActive = activeCategory === node.title;
              return (
                <div
                  key={node.title}
                  style={{
                    position: "absolute",
                    left: `calc(50% + ${node.x}px)`,
                    top: `calc(50% + ${node.y}px)`,
                    transform: "translate(-50%, -50%)",
                  }}
                  className="z-20 flex flex-col items-center gap-1"
                >
                  <button
                    type="button"
                    onMouseEnter={() => selectCategory(node.title)}
                    onMouseLeave={clearCategory}
                    className="w-11 h-11 sm:w-12 sm:h-12 rounded-full flex items-center justify-center border-2 transition-all duration-200 ease-out focus:outline-none shadow-md"
                    style={{
                      backgroundColor: isActive ? hexToRgba(color, 0.2) : "#ffffff",
                      borderColor: isActive ? color : hexToRgba(color, 0.5),
                      color: color,
                      boxShadow: isActive
                        ? `0 0 0 6px ${hexToRgba(color, 0.2)}, 0 10px 22px -4px ${hexToRgba(color, 0.5)}`
                        : "0 2px 6px rgba(15,23,42,0.12)",
                      transform: isActive ? "scale(1.2)" : "scale(1)",
                    }}
                  >
                    {categoryNodeIcons[node.title]}
                  </button>
                  <span
                    className="text-[10px] sm:text-[11px] font-bold whitespace-nowrap px-2 py-0.5 rounded-full transition-colors shadow-none"
                    style={{
                      color: isActive ? color : "#64748b",
                      backgroundColor: isActive ? hexToRgba(color, 0.12) : "rgba(255,255,255,0.8)",
                    }}
                  >
                    {categoryShortLabels[node.title]}
                  </span>
                </div>
              );
            })}

            {/* Bloomed Tech Sub-Icons */}
            {bloomIcons.map((item, i) => {
              const color = categoryColors[item.id];
              const isHovered = hoveredIcon?.name === item.name;
              return (
                <div
                  key={item.name}
                  style={{
                    position: "absolute",
                    left: `calc(50% + ${item.x}px)`,
                    top: `calc(50% + ${item.y}px)`,
                    transform: "translate(-50%, -50%)",
                  }}
                  className="z-30"
                >
                  <div
                    className="bloom-icon-inner"
                    style={{ animationDelay: `${i * 30}ms` }}
                  >
                    <button
                      type="button"
                      onMouseEnter={() => setHoveredIcon(item)}
                      onMouseLeave={() => setHoveredIcon(null)}
                      className="w-9 h-9 sm:w-10 sm:h-10 rounded-full flex items-center justify-center border-2 bg-white transition-all duration-150 ease-out focus:outline-none"
                      style={{
                        borderColor: isHovered ? color : hexToRgba(color, 0.5),
                        boxShadow: isHovered
                          ? `0 0 0 5px ${hexToRgba(color, 0.2)}, 0 8px 18px -4px ${hexToRgba(color, 0.5)}`
                          : "0 2px 4px rgba(15,23,42,0.15)",
                        transform: isHovered ? "scale(1.3)" : "scale(1)",
                      }}
                    >
                      {item.icon}
                    </button>
                  </div>
                </div>
              );
            })}

            {/* Expanded Center Information Hub */}
            <div className="absolute z-40 pointer-events-none flex items-center justify-center">
              <div className="tech-hub w-32 h-32 sm:w-36 sm:h-36 rounded-full bg-white/95 backdrop-blur-md border-2 border-indigo-100 flex flex-col items-center justify-center p-3 text-center transition-all duration-300">
                {hubContent ? (
                  <div key={hubContent.title} className="flex flex-col items-center w-full px-1">
                    <div
                      className="p-1.5 rounded-full border mb-1 flex items-center justify-center"
                      style={{
                        backgroundColor: hexToRgba(hubContent.color || "#6366f1", 0.1),
                        borderColor: hexToRgba(hubContent.color || "#6366f1", 0.35),
                      }}
                    >
                      {hubContent.type === "tech" ? (
                        hubContent.icon
                      ) : (
                        <span
                          className="block rounded-full"
                          style={{ width: 10, height: 10, backgroundColor: hubContent.color }}
                        />
                      )}
                    </div>
                    <span className="text-xs sm:text-sm font-extrabold text-slate-900 leading-tight">
                      {hubContent.title}
                    </span>
                    <hr className="w-8 my-1 border-slate-200" />
                    <p className="text-[10px] sm:text-[11px] text-slate-600 leading-tight line-clamp-3">
                      {hubContent.desc}
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-col items-center">
                    <span className="text-[9px] font-black uppercase tracking-wider text-indigo-600 leading-none">
                      Technology
                    </span>
                    <span className="text-sm font-extrabold text-slate-800 mt-0.5">
                      Hub
                    </span>
                    <hr className="w-8 my-1 border-slate-200" />
                    <span className="text-[10px] text-slate-400 font-medium">
                      Hover a category or tool
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Right Column: Category Cards */}
        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-4">
          {features.map((item, index) => (
            <div
              key={index}
              onMouseEnter={() => selectCategory(item.title)}
              onMouseLeave={clearCategory}
              className="group relative bg-white rounded-2xl p-5 shadow-sm hover:shadow-xl border border-slate-100 transition-all duration-300 flex flex-col justify-between overflow-hidden cursor-pointer"
            >
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${item.accent} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2.5">
                    <span className={`w-2.5 h-2.5 rounded-full ${item.circle}`} />
                    <h3 className="text-base font-bold text-slate-800 group-hover:text-slate-900 transition-colors">
                      {item.title}
                    </h3>
                  </div>
                  {item.hot && (
                    <span className="px-2 py-0.5 text-[10px] font-bold tracking-wide text-amber-600 bg-amber-50 rounded-full border border-amber-200">
                      HOT
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mb-3 leading-snug">
                  {categoryDescriptions[item.title]}
                </p>

                {/* Hoverable Tags that show descriptions in the Hub */}
                <div className="flex flex-wrap gap-1.5 mt-2">
                  {item.tags.map((tag, idx) => {
                    const isTagHovered = hoveredIcon?.name.toLowerCase() === tag.toLowerCase();
                    return (
                      <span
                        key={idx}
                        onMouseEnter={(e) => {
                          e.stopPropagation();
                          handleTagHover(item.title, tag);
                        }}
                        onMouseLeave={() => setHoveredIcon(null)}
                        className={`text-xs px-2.5 py-1 rounded-md font-medium border transition-all cursor-pointer ${item.badgeBg}`}
                        style={{
                          transform: isTagHovered ? "scale(1.08)" : "scale(1)",
                          boxShadow: isTagHovered ? "0 2px 8px rgba(0,0,0,0.12)" : "none",
                          fontWeight: isTagHovered ? "700" : "500",
                        }}
                      >
                        {tag}
                      </span>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

      </div>
    </section>
  );
};

export default Technologies;